import React, { FunctionComponent, useContext } from "react";
import { Stack, Typography, Container, CircularProgress, Alert } from "@mui/material";

import WorkshopButton from "../../components/workshops/WorkshopButton";

import { WorkshopDto } from "../../types/workshopTypes";

import useResponsive from "../../hooks/useResponsive";
import useWorkshops from "../../hooks/workshops/useWorkshops";

import WorkshopsOverviewTable from "../../components/workshops/WorkshopsOverviewTable";
import WorkshopsOverviewCard from "../../components/workshops/WorkshopsOverviewCard";
import { doesPermissionsHaveSomeOf } from "../../utils/authUtils";
import { AuthContext } from "../../context/auth-context/AuthContext";
import WorkshopPendingFeedbackOverview from "../../components/workshops/WorkshopPendingFeedbackOverview";

/**
 * Component to display a table or cards of all workshops
 * @returns Displays all workshops
 */
const WorkshopsOverview: FunctionComponent = () => {
  const { auth } = useContext(AuthContext);
  const { permissions } = auth;
  const hasWorkshopPermission = doesPermissionsHaveSomeOf(permissions, [4]);

  const isMobile = useResponsive("down", "sm");

  // Use react-query to fetch workshops
  const { workshops, isWorkshopsLoading, isWorkshopsError, pendingFeedbackForWorkshopInstances } = useWorkshops();

  if (isWorkshopsLoading) {
    return (
      <Container>
        <Stack direction="row" justifyContent="center" alignItems="center" sx={{ minHeight: "200px" }}>
          <CircularProgress />
        </Stack>
      </Container>
    );
  }

  if (isWorkshopsError) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 2 }}>
          Fehler beim Laden der Workshops
        </Alert>
      </Container>
    );
  }

  return (
    <Container>
      {pendingFeedbackForWorkshopInstances.length > 0 ? (
        <Stack direction={"column"} justifyContent={"space-between"} sx={{ mb: 2 }}>
          <Typography variant="h5" component="h1" gutterBottom fontWeight={"bold"}>
            Fehlendes Workshopfeedback
          </Typography>
          <WorkshopPendingFeedbackOverview pendingFeedbackForWorkshopInstances={pendingFeedbackForWorkshopInstances} />
        </Stack>
      ) : null}
      <Stack
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
        alignContent={"center"}
        sx={{ mb: 2 }}
      >
        <Typography variant="h5" component="h1" gutterBottom fontWeight={"bold"}>
          Alle Workshops
        </Typography>
        {hasWorkshopPermission ? <WorkshopButton /> : null}
      </Stack>
      {isMobile ? (
        <Stack direction={"column"} spacing={2}>
          {workshops?.map((workshop: WorkshopDto, index: number) => {
            return <WorkshopsOverviewCard workshop={workshop} key={index} />;
          })}
        </Stack>
      ) : (
        <WorkshopsOverviewTable workshops={workshops || []} />
      )}
    </Container>
  );
};

export default WorkshopsOverview;
